// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Cart, CartItem, CartItemWithMetadata } from "@coveo/headless/commerce";
import { loadCartItemsFromLocalStorage, saveCartItemsToLocalStorage } from "@DSG/Coveo/Contex/Engine";
import { Handler, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { CartResult, GetCartApiParameter } from "@insite/client-framework/Services/CartService";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = Handler<
    {
        shouldLoadFullCart?: boolean;
        replaceCart?: boolean;
        getPromotions?: boolean;
        getCartLines?: boolean;
        getCostCodes?: boolean;
    } & HasOnSuccess,
    {
        apiParameter: GetCartApiParameter;
        apiResult: CartResult;
        needFullCart: boolean;
    }
>;

// Store the previous cart state globally to track changes
let currentCartState = new Map<string, number>();

const CoveoCartLoadChainTap = (coveoCart: Cart): HandlerType => props => {
    const coveoLocalStorageCart = loadCartItemsFromLocalStorage() || [];

    const makeProductItemsForEvent = (cart: CartLineModel[] | null | undefined): CartItem[] => {
        if (!cart) {
            return [];
        }
    
        // Map cart lines to CartItem
        return cart.map((p: CartLineModel): CartItem => {
            const productId = p.productName ?? "";
            
            // Find corresponding product in localStorage
            const storedProduct = coveoLocalStorageCart.find(
                (item: CartItemWithMetadata) => item.productId === productId
            );
            
            return {
                productId: p.productName,
                name: storedProduct?.name || "",
                price: p.pricing?.actualPrice ?? 0,
                quantity: p.qtyOrdered ?? 0,
            };
        });
    };
   
    // Get current cart products from API result
    const products = props.apiResult.cart.cartLines;
    const convertedProducts = makeProductItemsForEvent(products);
    
    // Check if the API response includes cart lines
    // If not included, don't remove items from the cart
    const cartLinesIncludedInResponse = props.needFullCart || 
                                        props.parameter.getCartLines || 
                                        (props.apiParameter.expand && 
                                         props.apiParameter.expand.includes("cartLines"));
    
    // Create map of new cart state
    const newCartState = new Map<string, number>();
    convertedProducts.forEach(product => {
        newCartState.set(product.productId, product.quantity || 0);
    });

    // Only process product removals if we have cart lines in the response
    let productsToBeRemoved: CartItem[] = [];
    if (cartLinesIncludedInResponse) {
        // Find products that have been truly removed from the cart (not in new cart)
        productsToBeRemoved = [...currentCartState.keys()]
            .filter(productId => !newCartState.has(productId))
            .map(productId => {
                // Find the product in localStorage to get its details
                const product = coveoLocalStorageCart.find((p: CartItemWithMetadata) => p.productId === productId);
                if (product) {
                    return { 
                        productId: product.productId,
                        name: product.name || "",
                        price: product.price || 0,
                        quantity: 0 
                    } as CartItem;
                }
                // If not in localStorage, create a minimal product object with required fields
                return { 
                    productId, 
                    name: "", 
                    price: 0,
                    quantity: 0 
                } as CartItem;
            });
    } else {
        console.log("Cart lines not included in response, skipping product removal processing");
    }

    // Find products whose quantity has changed (increased or decreased)
    const productsWithChangedQty = convertedProducts.filter((product: CartItem) => {
        const previousQuantity = currentCartState.get(product.productId) || 0;
        const newQuantity = product.quantity || 0;
        const changed = newQuantity !== previousQuantity;

        return changed;
    });
    
    // Only send update events for products that actually changed
    if (productsWithChangedQty.length > 0) {
        productsWithChangedQty.forEach(product => {
            coveoCart.updateItemQuantity(product);
        });
    }
    
    // Only send remove events for products that were actually removed
    if (productsToBeRemoved.length > 0) {
        productsToBeRemoved.forEach(product => {
            coveoCart.updateItemQuantity(product);
        });
    }
    
    // Only update the current cart state if we have cart lines included
    if (cartLinesIncludedInResponse) {
        currentCartState = newCartState;
    }
    
    // Only update localStorage if we have actual cart items
    if (convertedProducts.length > 0) {
        // Calculate totals for the cart state
        const totalQuantity = convertedProducts.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const totalPrice = convertedProducts.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 0)), 0);
        
        // Update the localStorage with current cart state
        saveCartItemsToLocalStorage({ 
            items: convertedProducts,
            totalQuantity,
            totalPrice 
        });
    }    
};

export default CoveoCartLoadChainTap;